import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../css/CategoryPage.css";
import axios from "axios";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import { useAuth } from "../../context/auth";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CancelIcon from "@mui/icons-material/Cancel";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";

const UserOrders = () => {
    const [auth] = useAuth();
    const apiUrl = `${process.env.REACT_APP_API}`;
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isLoggedIn = !!auth.token;
    const userId = auth && auth.currentUser && auth.currentUser.id;
    const navigate = useNavigate();

    useEffect(() => {
        if (auth.token && userId) {
            getOrders();
        }
    }, [auth.token, userId]);

    const getOrders = async () => {
        try {
            setIsLoading(true);
            const { data } = await axios.get(`${apiUrl}/orders/${userId}`);

            // Sort orders by date (newest first)
            const sortedOrders = data.sort((a, b) =>
                new Date(b.createdAt) - new Date(a.createdAt)
            );

            setOrders(sortedOrders);
            setIsLoading(false);
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }
    };

    const getOrderStatusInfo = (status) => {
        switch (status) {
            case "Order Received":
                return {
                    icon: <HourglassEmptyIcon />,
                    color: "#ffc107",
                    bgColor: "#fff3cd",
                    text: "Processing",
                    description: "We're preparing your order for shipment"
                };
            case "Shipped":
                return {
                    icon: <LocalShippingIcon />,
                    color: "#17a2b8",
                    bgColor: "#d1ecf1",
                    text: "Shipped",
                    description: "Your order is on its way!"
                };
            case "Cancelled":
                return {
                    icon: <CancelIcon />,
                    color: "#dc3545",
                    bgColor: "#f8d7da",
                    text: "Cancelled",
                    description: "This order was cancelled"
                };
            case "Refunded":
                return {
                    icon: <MoneyOffIcon />,
                    color: "#856404",
                    bgColor: "#fff3cd",
                    text: "Refunded",
                    description: "Your refund has been processed. Expect it within 3-5 business days."
                };
            default:
                return {
                    icon: <ShoppingBagIcon />,
                    color: "#6c757d",
                    bgColor: "#e9ecef",
                    text: status,
                    description: ""
                };
        }
    };

    if (!isLoggedIn) {
        return (
            <>
                <Header />
                <div className="container my-5">
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="card shadow-lg border-0 text-center p-5">
                                <ShoppingBagIcon style={{ fontSize: "5rem", color: "#002E6A", margin: "0 auto" }} />
                                <h3 className="mt-4 mb-3">View Your Orders</h3>
                                <p className="text-muted mb-4">
                                    Please log in to view your order history and track your shipments.
                                </p>
                                <button
                                    className="btn btn-primary btn-lg px-5"
                                    onClick={() => navigate("/login")}
                                    style={{ backgroundColor: "#002E6A", borderColor: "#002E6A" }}
                                >
                                    Login to Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="container my-5">
                <div className="text-center mb-5">
                    <h1 style={{ color: "#002E6A", fontWeight: "700" }}>
                        <ShoppingBagIcon style={{ fontSize: "2.5rem", marginRight: "10px" }} />
                        My Orders
                    </h1>
                    <p className="text-muted">Track and manage your orders</p>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading your orders...</p>
                    </div>
                )}

                {/* No Orders */}
                {!isLoading && orders.length === 0 && (
                    <div className="text-center my-5">
                        <div className="card shadow-sm p-5">
                            <ShoppingBagIcon style={{ fontSize: "5rem", color: "#ccc", margin: "0 auto" }} />
                            <h4 className="mt-4">No orders yet</h4>
                            <p className="text-muted mb-4">
                                Start shopping to see your orders here!
                            </p>
                            <button
                                className="btn btn-primary btn-lg px-5 mx-auto"
                                onClick={() => navigate("/")}
                                style={{ backgroundColor: "#002E6A", borderColor: "#002E6A" }}
                            >
                                Start Shopping
                            </button>
                        </div>
                    </div>
                )}

                {/* Orders List */}
                {!isLoading && orders.length > 0 && (
                    <>
                        <div className="mb-3">
                            <small className="text-muted">
                                You have <strong>{orders.length}</strong> {orders.length === 1 ? 'order' : 'orders'}
                            </small>
                        </div>

                        {orders.map((order) => {
                            const statusInfo = getOrderStatusInfo(order.status);

                            return (
                                <div key={order._id} className="card shadow-sm mb-4 order-card">
                                    {/* Order Header */}
                                    <div
                                        className="card-header"
                                        style={{
                                            backgroundColor: statusInfo.bgColor,
                                            borderBottom: `3px solid ${statusInfo.color}`
                                        }}
                                    >
                                        <div className="row align-items-center">
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className="status-icon me-3"
                                                        style={{
                                                            color: statusInfo.color,
                                                            fontSize: "2rem"
                                                        }}
                                                    >
                                                        {statusInfo.icon}
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-0" style={{ color: statusInfo.color, fontWeight: "600" }}>
                                                            {statusInfo.text}
                                                        </h5>
                                                        <small style={{ color: statusInfo.color }}>
                                                            {statusInfo.description}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 text-md-end mt-2 mt-md-0">
                                                <small className="text-muted">
                                                    Order Date: <strong>{new Date(order.createdAt).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}</strong>
                                                </small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-body p-4">
                                        {/* Order Summary */}
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <h6 className="text-muted mb-2">Order Total</h6>
                                                <h3 className="text-success mb-0" style={{ fontWeight: "700" }}>
                                                    ${order.total}
                                                </h3>
                                            </div>
                                            <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                                {order.status === "Shipped" && order.trackingNumber && (
                                                    <div>
                                                        <p className="mb-2 text-muted">
                                                            <strong>USPS Tracking Number:</strong>
                                                        </p>
                                                        <a
                                                            href={`https://tools.usps.com/go/TrackConfirmAction?tRef=fullpage&tLc=3&text28777=&tLabels=${order.trackingNumber}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-info btn-sm"
                                                        >
                                                            <LocalShippingIcon fontSize="small" className="me-1" />
                                                            Track Package: {order.trackingNumber}
                                                        </a>
                                                    </div>
                                                )}
                                                {order.status === "Shipped" && (
                                                    <div className="mt-3">
                                                        <Link to="/reviews" className="btn btn-outline-primary btn-sm">
                                                            <RateReviewIcon fontSize="small" className="me-1" />
                                                            Write a Review
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Products */}
                                        <div>
                                            <h6 className="border-bottom pb-2 mb-3">
                                                Order Items ({order.products.length})
                                            </h6>
                                            <div className="row g-3">
                                                {order.products.map((p) => (
                                                    p.product ? (
                                                        <div key={p._id} className="col-12">
                                                            <div className="product-item">
                                                                <div className="row align-items-center">
                                                                    <div className="col-md-2 col-3 text-center">
                                                                        <Link to={`/products/${p.product._id}`}>
                                                                            <img
                                                                                src={p.product.images.front}
                                                                                alt={p.product.productName}
                                                                                className="img-fluid rounded"
                                                                                style={{
                                                                                    maxHeight: "100px",
                                                                                    objectFit: "contain",
                                                                                    cursor: "pointer"
                                                                                }}
                                                                                loading="lazy"
                                                                            />
                                                                        </Link>
                                                                    </div>
                                                                    <div className="col-md-7 col-9">
                                                                        <Link
                                                                            to={`/products/${p.product._id}`}
                                                                            className="text-decoration-none text-dark"
                                                                        >
                                                                            <h6 className="mb-1" style={{ fontWeight: "600" }}>
                                                                                {p.product.productName}
                                                                            </h6>
                                                                        </Link>
                                                                        <div className="product-details mt-2">
                                      <span className="badge bg-secondary me-2">
                                        Size: {p.selectedSize}
                                      </span>
                                                                            <span className="badge bg-info">
                                        Qty: {p.quantity}
                                      </span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-3 col-12 text-md-end mt-2 mt-md-0">
                                                                        <h5 className="mb-0 text-primary" style={{ fontWeight: "700" }}>
                                                                            ${p.product.salePrice > 0 ? p.product.salePrice : p.product.productPrice}
                                                                        </h5>
                                                                        <small className="text-muted">per item</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {order.products.indexOf(p) !== order.products.length - 1 && (
                                                                <hr className="my-3" />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div key={p._id} className="col-12">
                                                            <div className="product-item">
                                                                <div className="row align-items-center">
                                                                    <div className="col-12">
                                                                        <p className="text-muted mb-0">
                                                                            <em>Product no longer available</em>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
            <Footer />

            <style jsx>{`
        .order-card {
          border-radius: 12px;
          border: none;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .order-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
        }

        .product-item {
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 8px;
        }

        .product-image {
          transition: transform 0.3s ease;
        }

        .product-image:hover {
          transform: scale(1.1);
        }

        .badge {
          font-size: 0.85rem;
          padding: 0.4em 0.8em;
          font-weight: 500;
        }

        .status-icon {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @media (max-width: 768px) {
          .product-item {
            padding: 10px;
          }
        }
      `}</style>
        </>
    );
};

export default UserOrders;